/**
 * One list row (§B.1): icon disc · name over meta · display-serif figures.
 *
 * Markup only; every interaction is delegated to the listbox (PlaceList), so a
 * row re-renders only when its own props change. Hover light (.is-hover), the
 * keyboard's active ring (.is-active) and the bar's shrink (.is-unselecting)
 * are toggled on the element by the list, never through React state per frame.
 */
import { Fragment, memo, type CSSProperties, type JSX } from "react";
import { cx, Fig, Icon, Status } from "@/design";
import type { CategoryKind, Place } from "@/contracts/data";
import type { SortKey } from "@/contracts/store";
import { rowFigures, statusWord } from "./format";

type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };

export interface PlaceRowProps {
  place: Place;
  icon: string;
  kind?: CategoryKind;
  sort: SortKey;
  meta: string;
  label: string;
  title: string;
  selected: boolean;
  active: boolean;
  /** Arrival stagger index (0–10); 0 for rows that simply appear. */
  stagger: number;
}

export const optionId = (id: string): string => `ld-opt-${id}`;

function PlaceRowImpl({ place, icon, kind, sort, meta, label, title, selected, active, stagger }: PlaceRowProps): JSX.Element {
  const figs = rowFigures(place, sort, kind);
  const status = statusWord(place.status) ? place.status : null;
  const statusInMeta = status && figs.kind !== "none";
  const note = kind !== "future" ? place.route?.note : undefined;
  const style: CSSVars = { "--i": stagger };

  return (
    <div
      role="option"
      id={optionId(place.id)}
      aria-selected={selected}
      aria-label={label}
      title={title}
      data-id={place.id}
      className={cx("ld-row ld-rake ld-in ld-loc-panel-row", selected && "is-selected", active && "is-active")}
      style={style}
    >
      <span className="ld-loc-panel-disc" aria-hidden="true">
        <Icon name={icon} size={18} />
      </span>
      <span className="ld-loc-panel-text" aria-hidden="true">
        <span className="ld-loc-panel-name">{place.name}</span>
        <span className="ld-loc-panel-meta">
          {meta && <span className="ld-loc-panel-metatext">{meta}</span>}
          {statusInMeta && (
            <>
              {meta && <span className="ld-loc-panel-sep">·</span>}
              <Status s={status} className="ld-loc-panel-status" />
            </>
          )}
        </span>
      </span>
      <span className="ld-loc-panel-figs" aria-hidden="true">
        {figs.kind === "travel" && (
          <>
            <Fig
              className="ld-loc-panel-fig"
              value={figs.primary.map((s, i) => (
                <Fragment key={i}>
                  {i > 0 && " "}
                  {s.n}
                  <small>{s.u}</small>
                </Fragment>
              ))}
            />
            <span className="ld-loc-panel-fig2">
              {note && <Icon name="info" size={12} className="ld-loc-panel-note" />}
              {figs.secondary}
            </span>
          </>
        )}
        {figs.kind === "missing" && <span className="ld-loc-panel-dash">—</span>}
        {figs.kind === "none" && status && <Status s={status} className="ld-loc-panel-status" />}
      </span>
    </div>
  );
}

export const PlaceRow = memo(PlaceRowImpl);
